/**
 * Key-based data hook for CapabilityDrawer
 *
 * KEY-BASED PATTERN: Fetches capability data internally using capabilityKey,
 * eliminating race conditions from dual state sources. The URL param
 * (capabilityDrawerKey) is the single source of truth.
 *
 * This replaces the old pattern where:
 * 1. handleOpenCapabilityDrawer() called setCapability(capability) synchronously
 * 2. A useEffect in drawers/index.tsx called setCapability() when API completed
 *
 * When clicking quickly between rows, stale API responses would overwrite newer state.
 * Now the drawer fetches its own data based on the URL param key.
 *
 * @param capabilityKey - The capability name/key to fetch data for (from URL param capabilityDrawerKey)
 * @returns Capability data, loading state, and refetch function
 */

import { Capability, useCapabilities } from '@/hooks/useCapabilities';

export function useCapabilityDrawerData(capabilityKey: string) {
  // Fetch capabilities data by name
  const {
    data: capabilities = [],
    status,
    refetch,
  } = useCapabilities(
    { name: capabilityKey || '' },
    {
      enabled: Boolean(capabilityKey),
      staleTime: 0, // Always fetch fresh data for drawer
    }
  );

  // Find the matching capability by name
  const capability = (capabilities.find(
    (cap: Capability) => cap.name === capabilityKey
  ) || null) as Capability | null;

  return {
    capability,
    isLoading: status === 'pending',
    status,
    refetch,
  };
}
